<template>
  <component
    :is="tag"
    class="ui-button"
    :class="[
      `ui-button__${type}`,
      className,
      { 'ui-button__loading': loading },
      { 'ui-button__w100': w100 },
      { 'disabled': disabled },
    ]"
    :disabled="loading || disabled"
    :type="submit ? 'submit' : 'button'"
    @click="$emit('click', $event)"
  >
    <template v-if="loading">
      <loader />
    </template>
    <template v-else>
      <div
        v-if="$slots.icon"
        class="ui-button__icon"
      >
        <slot name="icon" />
      </div>
      <slot />
    </template>
  </component>
</template>

<script>
import Loader from '@/components/core/ui/Loader.vue'

export default {
  name: 'CoreButton',
  components: {
    Loader,
  },
  props: {
    tag: {
      type: String,
      default: 'button',
    },
    type: {
      type: String,
      default: 'green',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    className: {
      type: String,
      default: '',
    },
    submit: {
      type: Boolean,
    },
    w100: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['click'],
}
</script>

<style lang="scss">
.ui-button {
  background-color: map-get($colors, 'default');
  border: 1px solid transparent;
  border-radius: 0.16rem;
  padding: 0.12rem 0.48rem;
  min-height: 0.48rem;
  @include row;
  align-items: center;
  text-align: center;
  justify-content: center;

  cursor: pointer;

  @include typography('b2', 'bold');

  text-transform: uppercase;
  color: map-get($colors, 'white');
  transition: border-color .3s, background-color .3s, color .3s;

  &:not(.ui-button__loading):disabled,
  &:not(.ui-button__loading).disabled {
    background-color: map-get($colors, 'grey_12');
    color: map-get($colors, 'grey_30');
    border-color: map-get($colors, 'grey_12');
    cursor: default;
  }

  .rub {
    line-height: 1rem;
  }

  &:focus {
    outline: none;
  }

  .ui-button__icon {
    margin-right: 0.04rem;
  }

  .c-icon {
    display: flex;
    width: 0.12rem;
    height: 0.12rem;
    margin-right: 0.03em;
  }

  &__full-width {
    width: 100%;
  }

  &__from-var {
    color: var(--color, map-get($colors, 'white'));
    background-color: var(--bg-color, map-get($colors, 'default'));
    border-color: var(--bg-color, map-get($colors, 'default'));
    @include btn-hover {
      opacity: 0.75;
    }
  }

  &__green {
    color: map-get($colors, 'white');
    background-color: map-get($colors, 'default');
    border-color: map-get($colors, 'default');

    .a-loading {
      background-color: map-get($colors, 'white');
    }

    @include btn-hover {
      background-color: map-get($colors, 'default-light-75');
    }
  }

  &__dodger-blue {
    color: map-get($colors, 'white');
    background-color: map-get($colors, 'dodger-blue');
    border-color: map-get($colors, 'dodger-blue');

    @include btn-hover {
      opacity: 0.75;
    }
  }

  &__shamrock {
    color: map-get($colors, 'white');
    background-color: map-get($colors, 'shamrock');
    border-color: map-get($colors, 'shamrock');

    @include btn-hover {
      opacity: 0.75;
    }
  }

  &__emerald {
    color: map-get($colors, 'white');
    background-color: map-get($colors, 'emerald');
    border-color: map-get($colors, 'emerald');

    @include btn-hover {
      opacity: 0.75;
    }
  }

  &__mantis {
    color: map-get($colors, 'white');
    background-color: map-get($colors, 'mantis');
    border-color: map-get($colors, 'mantis');

    @include btn-hover {
      opacity: 0.75;
    }
  }

  &__sushi {
    color: map-get($colors, 'white');
    background-color: map-get($colors, 'sushi');
    border-color: map-get($colors, 'sushi');

    @include btn-hover {
      opacity: 0.75;
    }
  }

  &__key-lime-pie {
    color: map-get($colors, 'white');
    background-color: map-get($colors, 'key-lime-pie');
    border-color: map-get($colors, 'key-lime-pie');

    @include btn-hover {
      opacity: 0.75;
    }
  }

  &__supernova {
    color: map-get($colors, 'white');
    background-color: map-get($colors, 'supernova');
    border-color: map-get($colors, 'supernova');

    @include btn-hover {
      opacity: 0.75;
    }
  }

  &__pomegranat {
    color: map-get($colors, 'white');
    background-color: map-get($colors, 'pomegranat');
    border-color: map-get($colors, 'pomegranat');

    @include btn-hover {
      opacity: 0.75;
    }
  }

  &__blue {
    color: map-get($colors, 'white');
    background-color: map-get($colors, 'blue');
    border-color: map-get($colors, 'blue');

    .a-loading {
      background-color: map-get($colors, 'white');
    }
  }

  &__empty-blue {
    color: map-get($colors, 'blue');
    background-color: transparent;
    border-color: #e0eafc;
  }

  &__empty-white {
    color: map-get($colors, 'white');
    background-color: transparent;
    border-color: transparent;
  }

  &__danger {
    border-color: #dd4949;
    color: #dd4949;
    background-color: map-get($colors, 'white');
  }

  &__success {
    color: map-get($colors, 'green');
    background-color: map-get($colors, 'green_15');
    border-color: map-get($colors, 'green_15');
    .a-loading {
      background-color: map-get($colors, 'green');
    }
  }

  &__primary,
  &__yellow {
    color: map-get($colors, 'black');
    background-color: map-get($colors, 'yellow');
    border-color: map-get($colors, 'yellow');

    .a-loading {
      background-color: map-get($colors, 'green');
    }
  }

  &__white {
    color: map-get($colors, 'black');
    background-color: map-get($colors, 'white');
    border: 1px solid map-get($colors, 'gray_8');

    &:hover {
      background-color: map-get($colors, 'grey_8');
    }
  }

  &__white-grey {
    color: map-get($colors, 'grey_60');
    background-color: map-get($colors, 'white');
    border: 1px solid map-get($colors, 'gray_8');

    &:hover {
      background-color: map-get($colors, 'grey_8');
    }
  }

  &__brand {
    color: map-get($colors, 'default');
    background-color: map-get($colors, 'white');
    text-transform: uppercase;
  }

  &__secondary {
    color: map-get($colors, 'default');
    background-color: map-get($colors, 'green_15');
    border-color: map-get($colors, 'green_15');

    .a-loading {
      background-color: map-get($colors, 'default');
    }
  }

  &__grey {
    background-color: map-get($colors, 'grey_4');
    border-color: map-get($colors, 'grey_4');
    color: map-get($colors, 'grey_20');
    pointer-events: none;
    cursor: default;
    text-transform: unset !important;
    .a-loading {
      background-color: map-get($colors, 'black');
    }
  }

  &__ghost-grey {
    background-color: transparent;
    // border: 1px solid map-get($colors, 'grey_20');
    border: 1px solid transparent;
    color: map-get($colors, 'black');
    .a-loading {
      background-color: map-get($colors, 'black');
    }

    @include btn-hover{
      // border-color: map-get($colors, 'white');
      border-color: map-get($colors, 'grey_20');
    }
  }

  &__text-green {
    background-color: transparent;
    border-color: transparent;
    color: map-get($colors, 'default');
    .a-loading {
      background-color: map-get($colors, 'default');
    }
  }

  &__text-black {
    background-color: transparent;
    border-color: transparent;
    color: map-get($colors, 'black');
    .a-loading {
      background-color: map-get($colors, 'default');
    }
  }

  &__text-grey {
    background-color: transparent;
    border-color: transparent;
    color: map-get($colors, 'grey_20');
  }

  &__ghost-green {
    background-color: transparent;
    border: 1px solid map-get($colors, 'default');
    color: map-get($colors, 'default');
    .a-loading {
      background-color: map-get($colors, 'default');
    }
  }

  &__ghost-red {
    background-color: transparent;
    border: 1px solid map-get($colors, 'red_20');
    color: map-get($colors, 'red');
    .a-loading {
      background-color: map-get($colors, 'red');
    }
  }

  &__transparent-green {
    background-color: #E6F8E9;
    border: 1px solid #E6F8E9;
    color: map-get($colors, 'default');
  }
}

.ui-button-subscription {
  justify-content: space-between;
}

.ui-button-subscription__icon {
  .c-icon {
    width: 0.08rem !important;
    height: 0.05rem !important;
  }
}

.ui-button__loading {
  align-items: center;
  justify-content: center;
}

.ui-button__small {
  @include typography('b3', 'medium');

  min-height: 0.24rem;
  border-radius: 0.04rem;
  padding: 0.03rem 0.08rem;

  /*.c-icon {*/
  /*  width: 0.04rem;*/
  /*  height: 0.06rem;*/
  /*  margin-left: 0.03rem;*/
  /*}*/
}

.ui-button__medium {
  @include typography('b2', 'medium');

  padding: 0.06rem 0.12rem;
  min-height: 0.32rem;
  /*line-height: 0.2rem;*/

  .c-icon {
    width: 0.7em;
    height: 0.7em;
    /*margin-left: 0.5em;*/
  }
}

.ui-button__large {
  @include typography('b1', 'medium');
  padding: 0.08rem 0.12rem;
  height: 0.4rem;
  border-radius: 0.08rem;
}

.ui-button__w100 {
  width: 100%;
}

.lk-button.ui-button{
  @include typography('b1', 'bold');
  min-height: 0.4rem;
  padding: 0.08rem 0.2rem;

  border: 1px solid #CCF1D3;
  border-radius: 4px;

  box-sizing: border-box;

  text-transform: none;
}

.lk-header-button.ui-button {
  display: flex;
  flex-direction: row;
  align-items: center;

  @include typography('b2', 'medium');
  color: map-get($colors, 'default');

  min-height: 0;
  padding: 0.06rem 0.32rem;

  border-radius: 4px;
  border: 1px solid map-get($colors, 'green_20');

  text-transform: none;

  background: map-get($colors, 'green_5');

  box-shadow: 0 1px 4px rgba(map-get($colors, 'black_0'), 0.03);
  box-sizing: border-box;

  & .middle-button-text {
    margin: 0 0.04rem;
  }
}
</style>
